import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Stack,
  Divider,
  CircularProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { toast } from 'react-toastify';
import useMember from '../../hooks/useMember';

// Membership numbers follow the format COUNTRY-CENTER-MEMBER (e.g. 21-29-13511).
// The country code is the same for all own centers, so it is pre-filled.
const DEFAULT_COUNTRY_CODE = '21';
const HEX_REGEX = /^[0-9A-F]+$/;

// Map backend error codes to descriptive German toast messages.
const mapCreateError = err => {
  switch (err?.code) {
    case 'INVALID_CARD_NUMBER':
      return 'Ungültige Kartennummer. Format: Land-Center-Mitglied (z. B. 21-29-13511).';
    case 'CARD_NUMBER_IN_USE':
      return 'Diese Kartennummer existiert bereits.';
    case 'CARD_ID_IN_USE':
      return 'Eine der RFIDs ist bereits einem anderen Mitglied zugeordnet.';
    case 'INVALID_CARD_ID':
      return 'Eine RFID ist keine gültige Hex-ID.';
    case 'INSUFFICIENT_RIGHTS':
      return 'Fehlende Berechtigung zum Anlegen von Mitgliedern.';
    default:
      return 'Fehler beim Hinzufügen des Mitglieds.';
  }
};

function AddMemberDialog({ open, onClose, onCreated }) {
  const { createMember, resolveCenter } = useMember();

  // Card number segments and optional personal fields are uncontrolled (refs).
  const countryRef = useRef(null);
  const centerRef = useRef(null);
  const memberRef = useRef(null);
  const firstNameRef = useRef(null);
  const lastNameRef = useRef(null);
  const playerNameRef = useRef(null);
  const birthDateRef = useRef(null);

  // Dynamic RFID rows: stable ids keep each uncontrolled input's value on add/remove.
  const rfidIdCounter = useRef(0);
  const rfidInputRefs = useRef({});
  const [rfidRows, setRfidRows] = useState([0]);

  const [centerInfo, setCenterInfo] = useState(null); // { loading, foreign, branch, error }
  const [submitting, setSubmitting] = useState(false);

  const resolveTimerRef = useRef(null);
  const resolveSeqRef = useRef(0);

  // Start each dialog session with a single empty RFID row and no stale state.
  useEffect(() => {
    if (open) return;
    rfidInputRefs.current = {};
    rfidIdCounter.current = 0;
    setRfidRows([0]);
    setCenterInfo(null);
    setSubmitting(false);
    resolveSeqRef.current += 1;
    if (resolveTimerRef.current) clearTimeout(resolveTimerRef.current);
  }, [open]);

  // Debounced resolution of the entered center code to a branch (or foreign center).
  const scheduleResolve = useCallback(
    rawValue => {
      const code = String(rawValue || '').replace(/\D/g, '');
      if (resolveTimerRef.current) clearTimeout(resolveTimerRef.current);
      if (!code) {
        resolveSeqRef.current += 1;
        setCenterInfo(null);
        return;
      }
      setCenterInfo({ loading: true, foreign: false, branch: null });
      const seq = ++resolveSeqRef.current;
      resolveTimerRef.current = setTimeout(async () => {
        try {
          const result = await resolveCenter(code);
          if (seq !== resolveSeqRef.current) return; // a newer input superseded this lookup
          setCenterInfo({
            loading: false,
            foreign: Boolean(result?.foreign),
            branch: result?.branch || null,
          });
        } catch (err) {
          if (seq !== resolveSeqRef.current) return;
          setCenterInfo({ loading: false, error: true, foreign: false, branch: null });
        }
      }, 350);
    },
    [resolveCenter]
  );

  const handleDigitInput = e => {
    e.target.value = e.target.value.replace(/\D/g, '');
  };

  const handleCenterInput = e => {
    e.target.value = e.target.value.replace(/\D/g, '');
    scheduleResolve(e.target.value);
  };

  const addRfidRow = () => setRfidRows(prev => [...prev, ++rfidIdCounter.current]);

  const removeRfidRow = id => {
    setRfidRows(prev => (prev.length <= 1 ? prev : prev.filter(rowId => rowId !== id)));
    delete rfidInputRefs.current[id];
  };

  const handleClose = () => {
    if (submitting) return;
    onClose();
  };

  const handleSubmit = async () => {
    const countryCode = String(countryRef.current?.value || '').replace(/\D/g, '');
    const centerCode = String(centerRef.current?.value || '').replace(/\D/g, '');
    const memberCode = String(memberRef.current?.value || '').replace(/\D/g, '');

    if (!countryCode || !centerCode || !memberCode) {
      toast.error('Bitte die vollständige Kartennummer eingeben (Land-Center-Mitglied).');
      return;
    }
    const cardNumber = `${countryCode}-${centerCode}-${memberCode}`;

    // Collect RFIDs: upper-case, trim, drop empties, de-duplicate, validate hex.
    const seen = new Set();
    const cardIDs = [];
    for (const id of rfidRows) {
      const value = String(rfidInputRefs.current[id]?.value || '')
        .trim()
        .toUpperCase();
      if (!value) continue;
      if (!HEX_REGEX.test(value)) {
        toast.error(`Ungültige RFID „${value}". Nur Hex-Zeichen (0-9, A-F) erlaubt.`);
        return;
      }
      if (!seen.has(value)) {
        seen.add(value);
        cardIDs.push(value);
      }
    }

    const playerName = String(playerNameRef.current?.value || '').trim();
    if (playerName.length > 15) {
      toast.error('Codename darf maximal 15 Zeichen haben.');
      return;
    }

    const payload = { cardNumber, cardIDs };
    const firstName = String(firstNameRef.current?.value || '').trim();
    const lastName = String(lastNameRef.current?.value || '').trim();
    const birthDate = String(birthDateRef.current?.value || '').trim();
    if (firstName) payload.firstName = firstName;
    if (lastName) payload.lastName = lastName;
    if (playerName) payload.playerName = playerName;
    if (birthDate) payload.birthDate = birthDate;

    setSubmitting(true);
    try {
      const result = await createMember(payload);
      // useHttp stores the request and returns null when auth escalation is needed.
      if (result === null) {
        onClose();
        return;
      }
      toast.success('Mitglied wurde hinzugefügt.');
      if (typeof onCreated === 'function') onCreated();
      onClose();
    } catch (err) {
      toast.error(mapCreateError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const renderCenterStatus = () => {
    if (!centerInfo) {
      return (
        <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mt: 1 }}>
          Centercode eingeben, um die Filiale zu ermitteln.
        </Typography>
      );
    }
    if (centerInfo.loading) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
          <CircularProgress size={16} />
          <Typography variant='body2' color='text.secondary'>
            Filiale wird ermittelt…
          </Typography>
        </Box>
      );
    }
    if (centerInfo.error) {
      return (
        <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
          Filiale konnte nicht ermittelt werden.
        </Typography>
      );
    }
    if (centerInfo.foreign) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, color: 'warning.main' }}>
          <WarningAmberIcon fontSize='small' />
          <Typography variant='body2' sx={{ fontWeight: 600 }}>
            Fremdes Center
          </Typography>
        </Box>
      );
    }
    const branchName = centerInfo.branch?.locationName || centerInfo.branch?.longName;
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, color: 'success.main' }}>
        <CheckCircleIcon fontSize='small' />
        <Typography variant='body2' sx={{ fontWeight: 600 }}>
          {branchName ? `Eigenes Center: ${branchName}` : 'Eigenes Center'}
        </Typography>
      </Box>
    );
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth>
      <DialogTitle>Mitglied hinzufügen</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <Box>
            <Typography variant='subtitle2' gutterBottom>
              Kartennummer
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <TextField
                inputRef={countryRef}
                defaultValue={DEFAULT_COUNTRY_CODE}
                label='Land'
                size='small'
                onChange={handleDigitInput}
                inputProps={{ inputMode: 'numeric', maxLength: 3, style: { textAlign: 'center' } }}
                sx={{ width: 80 }}
              />
              <Typography sx={{ alignSelf: 'center', fontWeight: 700, fontSize: 20 }}>–</Typography>
              <TextField
                inputRef={centerRef}
                label='Centercode'
                size='small'
                autoFocus
                onChange={handleCenterInput}
                inputProps={{ inputMode: 'numeric', maxLength: 6 }}
                sx={{ width: 120 }}
              />
              <Typography sx={{ alignSelf: 'center', fontWeight: 700, fontSize: 20 }}>–</Typography>
              <TextField
                inputRef={memberRef}
                label='Mitgliedsnummer'
                size='small'
                onChange={handleDigitInput}
                inputProps={{ inputMode: 'numeric', maxLength: 12 }}
                sx={{ flex: 1, minWidth: 120 }}
              />
            </Box>
            {renderCenterStatus()}
          </Box>

          <Box>
            <Typography variant='subtitle2' gutterBottom>
              RFID-Karten
            </Typography>
            <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mb: 1 }}>
              Werden automatisch in Großbuchstaben gespeichert.
            </Typography>
            <Stack spacing={1}>
              {rfidRows.map((id, index) => (
                <Box key={id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TextField
                    inputRef={el => {
                      rfidInputRefs.current[id] = el;
                    }}
                    defaultValue=''
                    label={`RFID ${index + 1}`}
                    size='small'
                    fullWidth
                    inputProps={{ style: { textTransform: 'uppercase' } }}
                  />
                  <IconButton
                    aria-label='RFID entfernen'
                    onClick={() => removeRfidRow(id)}
                    disabled={rfidRows.length <= 1}
                  >
                    <DeleteOutlineIcon fontSize='small' />
                  </IconButton>
                </Box>
              ))}
            </Stack>
            <Button
              startIcon={<AddIcon />}
              onClick={addRfidRow}
              size='small'
              sx={{ mt: 1, textTransform: 'none' }}
            >
              Weitere RFID hinzufügen
            </Button>
          </Box>

          <Divider />

          <Box>
            <Typography variant='subtitle2' gutterBottom>
              Optionale Angaben
            </Typography>
            <Stack spacing={2}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField inputRef={firstNameRef} label='Vorname' size='small' fullWidth />
                <TextField inputRef={lastNameRef} label='Nachname' size='small' fullWidth />
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  inputRef={playerNameRef}
                  label='Codename'
                  size='small'
                  fullWidth
                  inputProps={{ maxLength: 15 }}
                />
                <TextField
                  inputRef={birthDateRef}
                  label='Geburtsdatum'
                  type='date'
                  size='small'
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
            </Stack>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          Abbrechen
        </Button>
        <Button
          variant='contained'
          onClick={handleSubmit}
          disabled={submitting}
          startIcon={submitting ? <CircularProgress size={16} /> : <AddIcon />}
        >
          Hinzufügen
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default AddMemberDialog;
